import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import { useMemo } from "react"
import { SalesAnalyticsTrend } from "../types"
import { formatAxisMnt, formatMnt } from "../utils"
import { AnalyticsCard } from "./AnalyticsCard"
import { AXIS_LABEL, AXIS_LINE, GRID_LINE, LEGEND, SERIES, TOOLTIP, YoYToggle } from "./chart-theme"

type Props = {
  data: SalesAnalyticsTrend | undefined
  isLoading: boolean
  compareYoY: boolean
  onToggleYoY: () => void
  title: string
  subtitle?: string
  currentYear: number
}

export function TrendChart({ data, isLoading, compareYoY, onToggleYoY, title, subtitle, currentYear }: Props) {
  const options = useMemo<Highcharts.Options>(() => {
    const labels = data?.data.current.map(b => b.label) ?? []
    const currentSeries = data?.data.current.map(b => b.value) ?? []
    const previousSeries = data?.data.previousYear?.map(b => b.value) ?? []

    const series: Highcharts.SeriesOptionsType[] = [
      {
        type     : "line",
        name     : String(currentYear),
        data     : currentSeries,
        color    : SERIES.current,
        lineWidth: 2.5,
        marker   : { enabled: true, radius: 4, symbol: "circle" },
        zIndex   : 2
      }
    ]
    if (compareYoY && previousSeries.length > 0) {
      series.push({
        type     : "line",
        name     : String(currentYear - 1),
        data     : previousSeries,
        color    : SERIES.previous,
        lineWidth: 1.5,
        dashStyle: "ShortDash",
        marker   : { enabled: true, radius: 3, symbol: "circle" },
        zIndex   : 1
      })
    }
    return {
      chart  : { type: "line", backgroundColor: "transparent", height: 260, style: { fontFamily: "inherit" } },
      title  : { text: undefined },
      credits: { enabled: false },
      accessibility: { enabled: false },
      legend : LEGEND,
      xAxis  : { categories: labels, gridLineWidth: 0, labels: { style: AXIS_LABEL }, lineColor: AXIS_LINE, tickColor: AXIS_LINE },
      yAxis  : {
        title: { text: undefined },
        gridLineColor: GRID_LINE,
        labels: { style: AXIS_LABEL, formatter() { return formatAxisMnt(this.value as number) } }
      },
      plotOptions: { line: { animation: { duration: 200 } } },
      tooltip: {
        ...TOOLTIP,
        formatter() {
          const points = this.points ?? []
          const body = points
            .map(p => p.y === null || p.y === undefined
              ? `${p.series.name}: —`
              : `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${formatMnt(p.y as number)}</b>`)
            .join("<br/>")
          return `<b>${this.x}</b><br/>${body}`
        }
      },
      series
    }
  }, [data, compareYoY, currentYear])

  return (
    <AnalyticsCard title={title} subtitle={subtitle} isLoading={isLoading} right={<YoYToggle active={compareYoY} onToggle={onToggleYoY} />}>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </AnalyticsCard>
  )
}
